import React, { memo, useMemo } from "react";
import EChartsReact from "@/components/EChartsReact";
import { buildMonthlyProjectionChartOption } from "./responseProjection.mjs";

const formatValue = (value, unit, compact = false) => {
  if (typeof value !== "number" || !Number.isFinite(value)) return "—";
  const options = {
    maximumFractionDigits: compact ? 2 : 0,
    ...(compact ? { notation: "compact" } : {}),
  };
  if (unit === "USD") {
    return new Intl.NumberFormat(undefined, {
      ...options,
      style: "currency",
      currency: "USD",
    }).format(value);
  }
  return `${new Intl.NumberFormat(undefined, options).format(value)}${unit ? ` ${unit}` : ""}`;
};

const ProjectionPanel = ({ projection }) => {
  const chartOption = useMemo(
    () => buildMonthlyProjectionChartOption(projection),
    [projection]
  );
  const hasCombinedTotal = projection.recommendedTotal !== null;
  const issues = [...projection.limitations, ...projection.excluded.map(
    (item) => `${item.label}: ${item.reason}`
  )];

  return (
    <section className="facility-ai-projection" aria-label={projection.title}>
      <div className="facility-ai-projection-header">
        <div>
          <span className="facility-ai-projection-eyebrow">Planning scenario</span>
          <h3>{projection.title}</h3>
        </div>
        <span className="facility-ai-projection-status">Evidence-derived</span>
      </div>

      <div className="facility-ai-projection-metrics">
        <div className="facility-ai-projection-metric facility-ai-projection-metric--primary">
          <span>{hasCombinedTotal ? "Working amount" : "Resources modeled"}</span>
          <strong>
            {hasCombinedTotal
              ? formatValue(projection.recommendedTotal, projection.unit, true)
              : projection.series.length}
          </strong>
        </div>
        <div className="facility-ai-projection-metric">
          <span>Planning sensitivity</span>
          <strong>
            {projection.lowerTotal !== null && projection.upperTotal !== null
              ? `${formatValue(projection.lowerTotal, projection.unit, true)}–${formatValue(
                  projection.upperTotal,
                  projection.unit,
                  true
                )}`
              : "Per resource"}
          </strong>
        </div>
        <div className="facility-ai-projection-metric">
          <span>Coverage</span>
          <strong>{projection.series.length} modeled · {projection.excluded.length} gap</strong>
        </div>
      </div>

      <div className="facility-ai-projection-chart">
        <div className="facility-ai-projection-section-title">Monthly planning allocation</div>
        <EChartsReact option={chartOption} style={{ width: "100%", height: 260 }} />
      </div>

      <div className="facility-ai-projection-series" role="list" aria-label="Resource planning amounts">
        {projection.series.map((item) => (
          <div className="facility-ai-projection-resource" role="listitem" key={item.resource}>
            <div className="facility-ai-projection-resource-heading">
              <strong>{item.label}</strong>
              <span>{item.baselineYear} actual → {item.projectionYear} plan</span>
            </div>
            <div className="facility-ai-projection-resource-value">
              {formatValue(item.recommended, item.unit, true)}
            </div>
            <div className="facility-ai-projection-resource-detail">
              Range {formatValue(item.lower, item.unit, true)}–{formatValue(item.upper, item.unit, true)}
              {item.growthAssumptionPercent !== null
                ? ` · ${item.growthAssumptionPercent >= 0 ? "+" : ""}${item.growthAssumptionPercent}% assumption`
                : ""}
            </div>
          </div>
        ))}
      </div>

      {issues.length ? (
        <div className="facility-ai-projection-notes">
          <div className="facility-ai-projection-section-title">Important limits</div>
          <ul>{issues.map((item) => <li key={item}>{item}</li>)}</ul>
        </div>
      ) : null}
      <div className="facility-ai-projection-disclaimer">
        Planning sensitivity, not an approved budget or statistical confidence interval.
      </div>
    </section>
  );
};

const AnswerProjectionPanel = ({ projections }) => {
  if (!Array.isArray(projections) || !projections.length) return null;
  return (
    <>
      {projections.map((projection) => (
        <ProjectionPanel key={projection.id} projection={projection} />
      ))}
    </>
  );
};

export default memo(AnswerProjectionPanel);
